import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import Radom from '../../state/Radom'
import { Container } from '../../components/Animations'
import SearchMultiselect from '../../components/SearchMultiselect'
import { Currencies, Currency } from '../../util/Currencies'
import User from '../../state/User'
import { errorToast, successToast } from '../../util/Util'
import Treasury from '../../state/Treasury'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './SettingsLayout'

const CurrenciesSettings = observer(() => {
  const [isLoading, setIsLoading] = useState(true)
  const [currency, setCurrency] = useState<Currency>()

  const getOrganization = async (): Promise<void> => {
    if (!User._user?.defaultOrganizationId) {
      return
    }

    setIsLoading(true)
    Radom.getOrganizationById(User._user.defaultOrganizationId)
      .then(r => {
        setCurrency(Currencies.filter(c => c.ticker === r.defaultCurrency)[0])
      })
      .catch(err => {
        console.error('Failed to load organization currency settings', err)
        setCurrency(undefined)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    getOrganization()
  }, [User._user?.defaultOrganizationId])

  return <Container>
    <SettingsPage>
      <SettingsHero>
        <div>
          <SettingsEyebrow>Organization settings</SettingsEyebrow>
          <SettingsTitle>Currencies</SettingsTitle>
          <SettingsSubtext>
            Set the default currency used for balances, reporting, and customer-facing payment totals.
          </SettingsSubtext>
        </div>
        <StatusPill $tone={currency ? 'green' : 'neutral'}>
          {currency?.ticker ?? 'No currency set'}
        </StatusPill>
      </SettingsHero>

      <SettingsCard>
        <SettingsCardHeader>
          <div>
            <SettingsCardTitle>Default currency</SettingsCardTitle>
            <SettingsCardDescription>
              This does not restrict accepted payment methods. It controls how amounts are displayed to your team.
            </SettingsCardDescription>
          </div>
        </SettingsCardHeader>
        <SettingsCardBody>
          <SettingsRows>
            <SettingsRow>
              <div>
                <SettingsRowTitle>Reporting currency</SettingsRowTitle>
                <SettingsRowDescription>
                  Used across dashboard summaries, balances, and hosted payment previews.
                </SettingsRowDescription>
              </div>
              <div style={{ minWidth: 260 }}>
                <SearchMultiselect
                  isLoading={isLoading}
                  disabled={false}
                  placeholder="Select currency"
                  itemArray={Currencies}
                  selectedItems={currency ? [currency] : []}
                  setSelectedItems={async c => {
                    if (!c[0]) return
                    try {
                      await Radom.updateSellerProfile({
                        defaultCurrency: c[0].ticker
                      })

                      await User.getOrganization(true)
                      Treasury.loadPriceQuotes(true)
                      successToast('Updated default currency')

                      setCurrency(c[0])
                    } catch (err) {
                      errorToast(err.response?.data?.error || err.message)
                    }
                  }}
                  keyExtractor={c => c.ticker}
                  labelExtractor={c =>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>{c.icon}{c.ticker}</div>}
                  isSingleSelect
                />
              </div>
            </SettingsRow>
          </SettingsRows>
        </SettingsCardBody>
      </SettingsCard>
    </SettingsPage>
  </Container>
})

export default CurrenciesSettings
